/**
 * web/src/screens/Notebook/index.tsx — the screen the router mounts. The
 * former 3,858-line god screen split into section modules (card vncVrGk1hc9i):
 * this file is orchestration only — it owns the GIFT_ACK mirror literal (the
 * probe reads it here), calls useNotebookState and composes the sections.
 */
import { useState } from 'react';
import { SubTabs, SubPanel } from '../../components/SubTabs';
import { TabHint } from '../../components/TabHint';
import { SkeletonBlock } from '../../ui/Skeleton';
import { KudosToast } from '../Requests';
import { Vault } from '../Vault';
import { type PearShare } from '../../lib/shareTarget';
import { GiftFlow } from './giftflow';
import { NotebookContext } from './context';
import { ConfirmSheet } from '../../ui/ConfirmSheet';
import { useNotebookState } from './hooks';
import { CaptureBar, Receipt, CaptureToast, UndoLine } from './capture';
import { DatePool } from './dates';
import { LinksBar } from './filters';
import { ClusterStrip } from './clusters';
import { NotebookCard } from './cards';
import { type EntryType, type NotebookComposeIntent, ENTRY_TILES, SAVABLE_TYPES, EMPTY_HINT, nbSubTabId, REMOVE_NOUN } from './types';
import { ENTRY_ICON } from './icons';
import './notebook.css';

export type { NotebookComposeIntent } from './types';

/** Mirrors GIFT_ACK in shared/gift-enrich.ts — the web bundle types its own
 *  surface rather than importing shared (same reason as INVITE_ALPHABET in
 *  Pair.tsx). scripts/probe-gift-enrich.mjs asserts the two stay identical,
 *  so a rewrite of the line in one place fails the probe instead of shipping
 *  a card that says something the server never sent. */
export const GIFT_ACK = "I've noted your gift idea. Please allow me some time to develop an initial list of suggestions. I'll be back to you shortly.";

/**
 * Shared Notebook — paste a link, it becomes a card for both partners
 * (epic szvNTWusHt3f), OR quick-add a typed plain-text entry (gift / memory /
 * date / note / idea — design v1.1 §5.3). The compose menu's Note / Idea tiles
 * deep-link here via `intent` and open the right quick-add composer.
 */
export function NotebookPanel({ intent, requestedCollection, requestedItem, onItemConsumed, pendingShare, onShareConsumed, onOpenDates, onOpenUs }: {
  intent?: NotebookComposeIntent | null;
  /** Quick link from the More menu (card yOcdjnR6pXBz): land on this
   *  collection. null = no request. Does NOT open a composer — the link is
   *  a filter, not an add. */
  requestedCollection?: string | null;
  /** Card 5wr2rXdlCSTh: a Search result names an item to open. The screen
   *  opens that item's context panel on arrival, then reports it consumed. */
  requestedItem?: string | null;
  onItemConsumed?: () => void;
  /** A share from another app (PWA share_target, card jZn7vpkB7EG1), parked
   *  until this screen could hold it. null = nothing parked. */
  pendingShare?: PearShare | null;
  /** Called once the parked share has been applied — the payload is dropped. */
  onShareConsumed?: () => void;
  /** Card anKalYfYvokw: the date footnote used to describe the route to
   *  Us → Dates; this opens it. App owns navigation, so the screen asks. */
  onOpenDates?: () => void;
  /** Gift flow (card NYJEJRCBfBGQ): "My taste, kept in Us" deep-links to the
   *  Us profile — same App-owned navigation as onOpenDates. */
  onOpenUs?: () => void;}) {
  const s = useNotebookState({ intent, requestedCollection, requestedItem, onItemConsumed, pendingShare, onShareConsumed, onOpenDates, giftAck: GIFT_ACK });
  const {
    items, failed, visible, viewMode, pickView, contextLive, collection, pickPill, linkTab,
    feedKeyDown, contextId, me, members, busyId, editBusy, suggestions, dateSaved,
    toggleDone, deleteEntry, startEdit, savePlace, setContextId, search, setSearch,
    pendingRemove, cancelRemove, confirmRemove,
  } = s;

  /* Card AieaJRs6sLga (Jonathan, email 2026-11-03): the collection search is a
     SERVER query, so a word that matches nothing comes back as `items = []` —
     byte-for-byte the same state as an empty notebook. Without this flag the
     empty-state wizard ("Nothing saved yet" + the six type tiles + the paste
     hint) painted inside an active search, which reads as the app having
     forgotten everything and invites a duplicate save. A live query is its own
     empty state, never the first-run one. */
  const searchQuery = search.trim();


  /* Card 3YypYdChpTZD (founder email 2026-10-31): the Vault is a NOTEBOOK tab
     now, not a standalone Explore destination. It is a VIEW of this surface,
     not a collection — `collection` keeps its entry-type meaning, so the
     capture bar, the filters and the list are untouched; this row simply swaps
     the panel for the existing vault screen. Unmounting it (any other tab)
     re-locks it, exactly as closing the old standalone panel did. */
  const [vaultOpen, setVaultOpen] = useState(false);
  const nbTabs = [
    ...ENTRY_TILES.map(t => ({ key: t.key as string, label: t.label })),
    { key: 'vault', label: 'Vault' },
  ];
  const activeTab: string = vaultOpen ? 'vault' : collection;
  const pickTab = (key: string) => {
    if (key === 'vault') {
      setVaultOpen(true);
      return;
    }
    setVaultOpen(false);
    pickPill(key as 'all' | EntryType);
  };

  /* Card 2wn9seit8zGi (PRD F5) — the List / Grid switch, rendered inside the
     links bar when that bar exists and on its own line when it does not (a
     typed tab has no links bar). One element, two placements. */
  const viewToggle = contextLive && items !== null && items.length > 0 ? (
    <div className="nb-view" role="group" aria-label="How to show the cards">
      <button
        type="button"
        className={`nb-view-btn${viewMode === 'list' ? ' is-on' : ''}`}
        aria-pressed={viewMode === 'list'}
        data-test="nb-view-list"
        onClick={() => pickView('list')}
      >
        List
      </button>
      <button
        type="button"
        className={`nb-view-btn${viewMode === 'grid' ? ' is-on' : ''}`}
        aria-pressed={viewMode === 'grid'}
        data-test="nb-view-grid"
        onClick={() => pickView('grid')}
      >
        Grid
      </button>
    </div>
  ) : null;

  return (
    <div className="nb-panel">
      {/* Card 2wn9seit8zGi (PRD L1-L4) — the feed is one zone and the context
          panel the other. Below 1100px `.nb-feed` is just a plain block and
          the panel is a slide-over; at >=1100px the pair becomes the
          three-zone shell (rail | feed | panel) the PRD asks for. */}
      <div className="nb-feed">
      <h2 className="nb-title">Notebook</h2>
      <p className="nb-lead">Save links, gifts, memories, dates — anything you both want to keep.</p>
      {/* First-run hint for the Archivist persona — names the one action that
          starts this tab: paste a link (TFeLwOAS1EeP). Rl0vzkZ2hdAe: it stops
          rendering once the notebook holds anything. Card 7VIbSBeJhvbQ gates
          it to the tabs that own the link action, so a typed tab (Gift /
          Memory / Date / Note / Idea) no longer tells you to paste a link. */}
      {linkTab && (
        <TabHint id="hint-notebook" hintKey="notebook">
          First move: paste a link you both want to keep — it lands here as a card you can both open.
        </TabHint>
      )}

      {/* ── Content-type nav — TABS at the top of the surface (card cMgatOnZPBZU).
          Same component every screen with a sub-nav uses: real tab semantics,
          horizontal scroll (a wrapped row reads as pills again), active tab
          emphasiSed with the Velvet Room mustard fill. It still picks the
          quick-add composer AND narrows the list. */}
      {/* ── The capture bar (card quWhYyrNiIbt item 1, PRD §6.2 C1-C5) ─────
          ONE sticky bar at the top of the feed for every type. The two
          composers it replaces (a typed quick-add and a link paste form) are
          gone: the type is a CHIP the text decides and the person can
          override, and who-can-see is a two-state control instead of the old
          Gift toggle. The ids the walks target (nb-quick / nb-quick-input /
          nb-quick-add) are unchanged, so the suite still drives the surface. */}
      {/* The bar and the collection tabs are ONE sticky block (PRD §7: "capture
          bar and tabs sticky"). Two independently sticky rows at top:0 would
          pin on top of each other, so the wrapper owns the pin and the tab row
          stops being sticky on its own. */}
      <div className="nb-sticky-top">
        <CaptureBar s={s} />

      <SubTabs
        name="nb-collection"
        label="Notebook collection"
        tabs={nbTabs}
        active={activeTab}
        onChange={pickTab}
        idFor={nbSubTabId}
      />
      </div>
      <SubPanel name="nb-collection" tabKey={activeTab} idFor={nbSubTabId}>
        {vaultOpen ? (
          <Vault onClose={() => setVaultOpen(false)} />
        ) : (
        <>
        <Receipt s={s} />
        {/* Gift flow (card NYJEJRCBfBGQ) — the four-step giver-private flow
            that replaced the old concierge pill strip on the Gift tab. */}
        {collection === 'gift' && <GiftFlow onOpenUs={onOpenUs} />}
        <DatePool s={s} />
        <LinksBar s={s} viewToggle={viewToggle} />
        <CaptureToast s={s} />
        <UndoLine s={s} />
        <ClusterStrip s={s} />
        {/* A typed tab has no links bar, so the toggle keeps its own line. */}
        {!linkTab && viewToggle}

        <div
          className={`nb-list${viewMode === 'grid' && contextLive ? ' is-grid' : ''}`}
          data-test="nb-list"
          data-view={viewMode === 'grid' && contextLive ? 'grid' : 'list'}
          onKeyDown={feedKeyDown}
        >
          {items === null && !failed && <SkeletonBlock lines={3} />}

          {failed && <p className="nb-empty">Couldn't load your collections.</p>}

          {items !== null && items.length === 0 && !failed && (
            searchQuery ? (
              /* Card AieaJRs6sLga: a zero-result search gets one honest line and
                 a way back — no wizard, no hint, no type tiles. The Clear button
                 is the escape hatch the bar's own field provides when it stays
                 mounted (filters.tsx), repeated here because this block is what
                 a person is actually looking at. */
              <div className="nb-empty-state" data-test="nb-search-empty">
                <p className="nb-empty-title">Nothing matches “{searchQuery}”.</p>
                <p className="nb-empty-hint">
                  Try a shorter word, or clear the search to see everything you have saved.
                </p>
                <button
                  type="button"
                  className="nb-search-clear"
                  data-test="nb-search-clear"
                  onClick={() => setSearch('')}
                >
                  Clear search
                </button>
              </div>
            ) : (
            <div className="nb-empty-state">
              <p className="nb-empty-title">Nothing saved yet.</p>
              {/* Card 7VIbSBeJhvbQ: the trailer / album line is LINK copy — it
                  only makes sense on the tabs that take a link. A typed tab gets
                  its own first-move line instead of being told to drop a link. */}
              <p className="nb-empty-hint">{EMPTY_HINT[collection] ?? EMPTY_HINT.all}</p>
              {/* Card anKalYfYvokw (IA phase 2): the surface never said what it
                  is. The strip below IS the collection list — the same six
                  savable types the tabs carry, with 'all' deliberately absent
                  because "everything" is not a thing you can save. Tapping a
                  tile picks that collection (and narrows the capture chip),
                  so the empty state teaches the model instead of describing it. */}
              <div className="nb-type-strip" data-test="nb-type-strip">
                {SAVABLE_TYPES.map(t => (
                  <button
                    key={t.key}
                    type="button"
                    className={`nb-type-tile nb-type-tile-${t.key}`}
                    data-test="nb-type-tile"
                    data-type={t.key}
                    onClick={() => pickPill(t.key)}
                  >
                    <span className="nb-icon" aria-hidden="true">{ENTRY_ICON[t.key]}</span>
                    <span className="nb-type-tile-label">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
            )
          )}

          {items !== null && items.length > 0 && visible.length === 0 && (
            <p className="nb-empty">No cards in this filter yet.</p>
          )}
          {visible.map(item => (
            <NotebookCard key={item.itemId} item={item} s={s} giftAck={GIFT_ACK} />
          ))}
        </div>

        {me === null && <p className="nb-empty">Log in to see the shared notebook.</p>}

        {/* PRD §6.1: "Saved to your date ideas — it'll come up when you plan
            together." The same portalled celebration primitive the kudos card
            and the chore tick-off use (card FktjQ7CT4keo), text-only. */}
        {dateSaved && (
          <KudosToast
            glyph="💡"
            label="Saved to your date ideas — it'll come up when you plan together."
            variant="shimmer"
          />
        )}
        </>
        )}
      </SubPanel>
      </div>{/* /.nb-feed */}

      {/* Card 2wn9seit8zGi (PRD L1/L4/P1) — the third zone. Renders only at
          >=760px; at >=1100px it is the sticky right column, between 760 and
          1100 it becomes the slide-over. Below that it does not exist and the
          phone keeps the layout it has always had. */}
      {contextLive && (
        <NotebookContext
          item={(items ?? []).find(i => i.itemId === contextId) ?? null}
          suggestions={suggestions}
          recent={[...(items ?? [])].sort((a, b) => b.createdAt - a.createdAt).slice(0, 4)}
          members={members}
          meId={me?.memberId ?? null}
          busy={busyId !== null || editBusy}
          onClose={() => setContextId(null)}
          onOpen={id => setContextId(id)}
          onDone={item => void toggleDone(item)}
          onRemove={item => void deleteEntry(item)}
          onEdit={item => startEdit(item)}
          onPlaceSave={(item, place) => savePlace(item, place)}
        />
      )}
      {/* Card snBw8Ua7Wnmc — one confirm sheet for every Remove in the
          Collection. The row's Remove only asks; this decides. */}
      <ConfirmSheet
        open={pendingRemove !== null}
        title={`Remove this ${pendingRemove ? REMOVE_NOUN[pendingRemove.entryType] ?? 'entry' : 'entry'}?`}
        body="It cannot be undone once the short undo window closes."
        onCancel={cancelRemove}
        onConfirm={() => void confirmRemove()}
      />
    </div>
  );
}
